import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "../Components/ui/card";
import { Button } from "../Components/ui/button";
import { Badge } from "../Components/ui/badge";
import { useLanguage } from "../contexts/LanguageContext";
import {
  Users,
  Globe,
  ArrowRight,
  Mail,
  Zap,
  Shield,
  Leaf,
  Calendar,
  Eye
} from "lucide-react";

const team = [
  {
    name: "Alex Thompson",
    avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=300&h=300&fit=crop&crop=face"
  },
  {
    name: "Sarah Martinez",
    avatar: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&h=300&fit=crop&crop=face"
  },
  {
    name: "David Chen",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=300&h=300&fit=crop&crop=face"
  },
  {
    name: "Lisa Rodriguez",
    avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face"
  }
];

// Render team cards to avoid unused variable warnings
const TeamGrid = ({ copy }) => (
  <section className="py-12 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <h3 className="text-2xl font-bold text-gray-900 mb-6">{copy.teamTitle}</h3>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {team.map((person, index) => (
          <div key={person.name} className="bg-white p-6 rounded-xl shadow-sm">
            <div className="flex items-center space-x-4">
              <img src={person.avatar} alt={person.name} className="w-16 h-16 rounded-full object-cover" />
              <div>
                <div className="font-semibold text-gray-900">{person.name}</div>
                <div className="text-sm text-gray-600">{copy.team[index].title}</div>
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4">{copy.team[index].bio}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const values = [
  {
    icon: Zap,
    color: "emerald"
  },
  {
    icon: Users,
    color: "green"
  },
  {
    icon: Leaf,
    color: "emerald"
  },
  {
    icon: Shield,
    color: "emerald"
  }
];

// NavLink component for navigation with scroll to top
const NavLink = ({ to, children, className = "", ...props }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = (e) => {
    e.preventDefault();
    if (location.pathname === to) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(to);
      // Use setTimeout to ensure navigation completes before scrolling
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
    }
  };

  return (
    <a href={to} onClick={handleClick} className={className} {...props}>
      {children}
    </a>
  );
};

export default function About() {
  const { t } = useLanguage();
  const copy = t("about");

  return (
    <div className="min-h-screen bg-white">
      {/* Brand & Company Information Section */}
      <section className="py-12 bg-white border-b border-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-emerald-50/50 rounded-2xl p-8 md:p-12 border border-emerald-100 shadow-sm animate-fade-in">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">{copy.company.title}</h2>

            <div className="grid md:grid-cols-2 gap-8 items-start">
              <div className="space-y-4">
                <p className="text-lg text-gray-700 leading-relaxed">
                  {copy.company.intro} <span className="font-semibold text-emerald-700">Guangzhou Wenyuanshiye Information Technology Co., Ltd.</span>
                </p>
                <p className="text-gray-600">
                  {copy.company.body}
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2 ml-2">
                  <li>{copy.company.product}</li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-xl border border-emerald-100 space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="mt-1 bg-emerald-100 p-1.5 rounded-lg text-emerald-600">
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{copy.company.companyLabel}</p>
                    <p className="text-gray-800 font-medium">Guangzhou Wenyuanshiye Information Technology Co., Ltd.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="mt-1 bg-emerald-100 p-1.5 rounded-lg text-emerald-600">
                    <Globe className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{copy.company.locationLabel}</p>
                    <p className="text-gray-800 font-medium">Guangzhou, China</p>
                  </div>
                </div>

                <div className="flex items-start space-x-3">
                  <div className="mt-1 bg-emerald-100 p-1.5 rounded-lg text-emerald-600">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{copy.company.contactLabel}</p>
                    <a href="mailto:business@intelevia.com" className="text-emerald-600 hover:text-emerald-700 font-medium transition-colors">
                      business@intelevia.com
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Section */}
      <section id="about-hero" className="py-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 border-emerald-600 text-emerald-600 animate-bounce-in">
              {copy.hero.badge}
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 animate-slide-in-up">
              <span className="text-gradient animate-gradient-shift">{t("interface.aboutTitle")}</span><br />
              {copy.hero.titleTail}
            </h1>
            <p className="text-2xl text-gray-700 max-w-4xl mx-auto animate-fade-in leading-relaxed font-medium">
              {copy.hero.body}
            </p>
          </div>


        </div>
      </section>

      {/* Kylendar Product Showcase */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-slide-in-left">
              <img
                src="/cover1.jpg"
                alt={t("accessibility.calendar")}
                className="w-full h-auto rounded-lg shadow-lg animate-float"
              />
            </div>
            <div className="animate-slide-in-right">
              <Badge variant="outline" className="mb-4 border-emerald-600 text-emerald-600 animate-bounce-in">
                {copy.showcase.badge}
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {copy.showcase.titleLead} <span className="text-gradient animate-gradient-shift">Kylendar</span>
              </h2>
              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {copy.showcase.body}
              </p>

              <div className="space-y-4 mb-8">
                <div className="flex items-center animate-fade-in" style={{ animationDelay: '0.1s' }}>
                  <Calendar className="w-5 h-5 text-emerald-600 mr-3 animate-wiggle" />
                  <span className="text-gray-700">{copy.showcase.features[0]}</span>
                </div>
                <div className="flex items-center animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  <Leaf className="w-5 h-5 text-emerald-600 mr-3 animate-wiggle" />
                  <span className="text-gray-700">{copy.showcase.features[1]}</span>
                </div>
                <div className="flex items-center animate-fade-in" style={{ animationDelay: '0.3s' }}>
                  <Eye className="w-5 h-5 text-emerald-600 mr-3 animate-wiggle" />
                  <span className="text-gray-700">{copy.showcase.features[2]}</span>
                </div>
                <div className="flex items-center animate-fade-in" style={{ animationDelay: '0.4s' }}>
                  <Zap className="w-5 h-5 text-emerald-600 mr-3 animate-wiggle" />
                  <span className="text-gray-700">{copy.showcase.features[3]}</span>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <NavLink to="/kylendar" className="w-full sm:w-auto">
                  <Button className="w-full gradient-green text-white hover:opacity-90 animate-pulse-glow hover:animate-wiggle">
                    {copy.showcase.learnMore}
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </NavLink>
                <a
                  href="https://kylendar.intelevia.com/us/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto"
                >
                  <Button variant="outline" className="w-full border-emerald-600/30 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-600 transition-all duration-300 hover:scale-[1.02] shadow-sm group">
                    <span className="relative flex h-2 w-2 mr-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    {copy.showcase.launch}
                    <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-slide-in-left">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                {copy.story.titleLead} <span className="text-gradient animate-gradient-shift">{copy.story.titleAccent}</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {copy.story.paragraphs[0]}
              </p>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                {copy.story.paragraphs[1]}
              </p>
            </div>
            <div className="relative animate-slide-in-right">
              <div className="bg-gradient-to-br from-emerald-100 to-green-100 rounded-3xl p-8 animate-float">
                <img
                  src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&h=400&fit=crop"
                  alt={t("accessibility.team")}
                  className="w-full h-80 object-cover rounded-2xl animate-scale-in"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Vision */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge variant="outline" className="mb-4 border-emerald-600 text-emerald-600 animate-bounce-in">
              {copy.vision.badge}
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 animate-slide-in-up">
              {copy.vision.titleLead} <span className="text-gradient animate-gradient-shift">{copy.vision.titleAccent}</span>
            </h2>

            {/* Team Section (rendered from team data) */}
            <TeamGrid copy={copy} />

            {/* Our Vision GIF */}
            <div className="max-w-4xl mx-auto mb-12 animate-scale-in">
              <div className="relative">
                <img
                  src="/our_Vison.gif"
                  alt={t("accessibility.vision")}
                  className="w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-500 animate-float"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="prose prose-lg prose-gray max-w-none animate-fade-in">
              <p className="text-xl text-gray-700 mb-8 leading-relaxed">
                {copy.vision.paragraphs[0]}
              </p>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {copy.vision.paragraphs[1]}
              </p>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {copy.vision.paragraphs[2]}
              </p>

              <div className="bg-white rounded-2xl p-8 shadow-lg mb-8 animate-scale-in">
                <p className="text-lg text-gray-600 mb-4 leading-relaxed italic">
                  {copy.vision.anecdote}
                </p>
                <blockquote className="text-xl text-emerald-600 font-medium text-center border-l-4 border-emerald-500 pl-6">
                  {copy.vision.quote}
                </blockquote>
              </div>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {copy.vision.paragraphs[3]}
              </p>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {copy.vision.paragraphs[4]}
              </p>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {copy.vision.paragraphs[5]}
              </p>

              <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                {copy.vision.paragraphs[6]}
              </p>

              <div className="text-center my-12">
                <p className="text-2xl font-bold text-emerald-600 mb-4 animate-pulse-glow">
                  {copy.vision.beliefLead}
                </p>
                <p className="text-3xl font-bold text-gray-900 animate-gradient-shift">
                  {copy.vision.beliefTitle}
                </p>
              </div>

              <p className="text-lg text-gray-600 leading-relaxed text-center">
                {copy.vision.paragraphs[7]}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 animate-slide-in-up">
              {copy.values.titleLead} <span className="text-gradient animate-gradient-shift">{copy.values.titleAccent}</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto animate-fade-in">
              {copy.values.body}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="border-0 shadow-lg hover-lift text-center animate-scale-in" style={{ animationDelay: `${index * 0.1}s` }}>
                <CardContent className="p-8">
                  <div className={`w-16 h-16 bg-${value.color}-100 rounded-2xl flex items-center justify-center mx-auto mb-6 animate-pulse-glow hover:animate-wiggle`}>
                    <value.icon className={`w-8 h-8 text-${value.color}-600`} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">
                    {copy.values.items[index].title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {copy.values.items[index].description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>



      {/* FAQ Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 animate-slide-in-up">
              {copy.faq.titleLead} <span className="text-gradient animate-gradient-shift">{copy.faq.titleAccent}</span>
            </h2>
            <p className="text-lg text-gray-600 animate-fade-in">
              {copy.faq.body}
            </p>
          </div>

          <div className="space-y-4">
            {/* FAQ Item 1 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[0].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {copy.faq.items[0].answer}
                  </p>
                </div>
              </details>
            </div>

            {/* FAQ Item 2 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[1].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {copy.faq.items[1].answer}
                  </p>
                </div>
              </details>
            </div>

            {/* FAQ Item 3 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[2].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {copy.faq.items[2].answer}
                  </p>
                </div>
              </details>
            </div>

            {/* FAQ Item 4 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '0.4s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[3].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <ol className="text-gray-600 leading-relaxed space-y-2 list-decimal list-inside">
                    {copy.faq.items[3].answer.map((item) => <li key={item}>{item}</li>)}
                  </ol>
                </div>
              </details>
            </div>

            {/* FAQ Item 5 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '0.5s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[4].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <ol className="text-gray-600 leading-relaxed space-y-2 list-decimal list-inside">
                    {copy.faq.items[4].answer.map((item) => <li key={item}>{item}</li>)}
                  </ol>
                </div>
              </details>
            </div>

            {/* FAQ Item 6 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '0.6s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[5].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    {copy.faq.items[5].answer.intro}
                  </p>
                  <ol className="text-gray-600 leading-relaxed space-y-2 list-decimal list-inside">
                    {copy.faq.items[5].answer.modes.map(([name, detail]) => <li key={name}><strong>{name}</strong> — {detail}</li>)}
                  </ol>
                  <p className="text-gray-600 leading-relaxed mt-4">
                    {copy.faq.items[5].answer.outro}
                  </p>
                </div>
              </details>
            </div>

            {/* FAQ Item 7 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '0.7s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[6].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {copy.faq.items[6].answer}
                  </p>
                </div>
              </details>
            </div>

            {/* FAQ Item 8 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '0.8s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[7].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {copy.faq.items[7].answer}
                  </p>
                </div>
              </details>
            </div>

            {/* FAQ Item 9 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '0.9s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[8].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {copy.faq.items[8].answer}
                  </p>
                </div>
              </details>
            </div>

            {/* FAQ Item 10 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '1.0s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[9].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {copy.faq.items[9].answer}
                  </p>
                </div>
              </details>
            </div>

            {/* FAQ Item 11 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '1.1s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[10].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {copy.faq.items[10].answer}
                  </p>
                </div>
              </details>
            </div>

            {/* FAQ Item 12 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '1.2s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[11].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {copy.faq.items[11].answer}
                  </p>
                </div>
              </details>
            </div>

            {/* FAQ Item 13 */}
            <div className="bg-white rounded-lg shadow-sm animate-fade-in" style={{ animationDelay: '1.3s' }}>
              <details className="group">
                <summary className="flex justify-between items-center p-6 cursor-pointer hover:bg-gray-50 transition-colors">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {copy.faq.items[12].question}
                  </h3>
                  <svg className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="px-6 pb-6">
                  <p className="text-gray-600 leading-relaxed">
                    {copy.faq.items[12].answer}
                  </p>
                </div>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 gradient-green">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 animate-slide-in-up">
            {copy.cta.titleLead} <span className="text-emerald-200 animate-gradient-shift">{copy.cta.titleAccent}</span>
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto animate-fade-in">
            {copy.cta.body}
          </p>
          <NavLink to="/contact">
            <Button size="lg" variant="outline" className="bg-white text-emerald-600 hover:bg-gray-50 animate-pulse-glow hover:animate-wiggle">
              {copy.cta.button}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </NavLink>
        </div>
      </section>
    </div>
  );
}
